import { AxiosError } from 'axios'

// Defensive import - Datadog might be blocked by WAF or fail to load
let datadogLogs: typeof import('@datadog/browser-logs').datadogLogs | null =
    null
try {
    // Dynamic import to prevent hard failure if Datadog is blocked
    const datadog = require('@datadog/browser-logs')
    datadogLogs = datadog.datadogLogs
} catch (error) {
    console.warn('Datadog logs unavailable:', error)
}

// API Error types
export enum ApiErrorCode {
    // Network errors
    NETWORK_ERROR = 'NETWORK_ERROR',
    TIMEOUT = 'TIMEOUT',
    ABORTED = 'ABORTED',

    // Auth errors
    UNAUTHORIZED = 'UNAUTHORIZED',
    FORBIDDEN = 'FORBIDDEN',
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',

    // Client errors
    BAD_REQUEST = 'BAD_REQUEST',
    NOT_FOUND = 'NOT_FOUND',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    RATE_LIMITED = 'RATE_LIMITED',

    // Server errors
    SERVER_ERROR = 'SERVER_ERROR',
    SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

    // Excel/Office API errors
    EXCEL_BUSY = 'EXCEL_BUSY',
    EXCEL_ERROR = 'EXCEL_ERROR',
    STARTUP_PERMISSION_ERROR = 'STARTUP_PERMISSION_ERROR',

    // Unknown
    UNKNOWN = 'UNKNOWN',
}

export interface ApiErrorDetails {
    code: ApiErrorCode
    message: string
    userMessage: string
    status?: number
    retryable: boolean
    originalError?: Error
    context?: Record<string, unknown>
}

export class ApiError extends Error {
    public readonly code: ApiErrorCode
    public readonly userMessage: string
    public readonly status?: number
    public readonly retryable: boolean
    public readonly originalError?: Error
    public readonly context?: Record<string, unknown>

    constructor(details: ApiErrorDetails) {
        super(details.message)
        this.name = 'ApiError'
        this.code = details.code
        this.userMessage = details.userMessage
        this.status = details.status
        this.retryable = details.retryable
        this.originalError = details.originalError
        this.context = details.context

        // Maintains proper stack trace in V8 environments
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError)
        }
    }

    toJSON() {
        return {
            name: this.name,
            code: this.code,
            message: this.message,
            userMessage: this.userMessage,
            status: this.status,
            retryable: this.retryable,
            context: this.context,
        }
    }
}

// User-friendly error messages
const USER_MESSAGES: Record<ApiErrorCode, string> = {
    [ApiErrorCode.NETWORK_ERROR]:
        'Unable to connect to the server. Please check your internet connection and try again.',
    [ApiErrorCode.TIMEOUT]:
        'The request took too long. Please try again or contact support if the issue persists.',
    [ApiErrorCode.ABORTED]: 'The request was cancelled.',
    [ApiErrorCode.UNAUTHORIZED]:
        'Your session has expired. Please log in again.',
    [ApiErrorCode.FORBIDDEN]:
        "You don't have permission to perform this action.",
    [ApiErrorCode.TOKEN_EXPIRED]:
        'Your session has expired. Please log in again.',
    [ApiErrorCode.BAD_REQUEST]:
        'There was a problem with your request. Please check your input and try again.',
    [ApiErrorCode.NOT_FOUND]:
        'The requested resource was not found. It may have been moved or deleted.',
    [ApiErrorCode.VALIDATION_ERROR]: 'Please check your input and try again.',
    [ApiErrorCode.RATE_LIMITED]:
        'Too many requests. Please wait a moment and try again.',
    [ApiErrorCode.SERVER_ERROR]:
        'Something went wrong on our end. Please try again later.',
    [ApiErrorCode.SERVICE_UNAVAILABLE]:
        'The service is temporarily unavailable. Please try again later.',
    [ApiErrorCode.EXCEL_BUSY]:
        'Excel is busy with another operation. Please wait a moment and try again.',
    [ApiErrorCode.EXCEL_ERROR]: 'An Excel operation failed. Please try again.',
    [ApiErrorCode.STARTUP_PERMISSION_ERROR]:
        'Unable to set startup behavior due to insufficient permissions.',
    [ApiErrorCode.UNKNOWN]:
        'An unexpected error occurred. Please try again or contact support.',
}

/**
 * Parse an Axios error into a standardized ApiError
 */
export function parseApiError(
    error: unknown,
    context?: Record<string, unknown>
): ApiError {
    // Handle AbortError / cancellation
    if (error instanceof Error && error.name === 'CanceledError') {
        return new ApiError({
            code: ApiErrorCode.ABORTED,
            message: 'Request was cancelled',
            userMessage: USER_MESSAGES[ApiErrorCode.ABORTED],
            retryable: false,
            originalError: error,
            context,
        })
    }

    // Handle Axios errors
    if (isAxiosError(error)) {
        const axiosError = error as AxiosError

        // Network error (no response received)
        if (!axiosError.response) {
            if (axiosError.code === 'ECONNABORTED') {
                return new ApiError({
                    code: ApiErrorCode.TIMEOUT,
                    message: 'Request timeout',
                    userMessage: USER_MESSAGES[ApiErrorCode.TIMEOUT],
                    retryable: true,
                    originalError: axiosError,
                    context,
                })
            }

            return new ApiError({
                code: ApiErrorCode.NETWORK_ERROR,
                message: axiosError.message || 'Network error',
                userMessage: USER_MESSAGES[ApiErrorCode.NETWORK_ERROR],
                retryable: true,
                originalError: axiosError,
                context,
            })
        }

        // Parse based on status code
        const status = axiosError.response.status
        const responseData = axiosError.response.data as
            | Record<string, unknown>
            | undefined

        // Extract server error message if available
        const serverMessage =
            responseData?.message || responseData?.error || axiosError.message

        const errorInfo = getErrorInfoFromStatus(
            status,
            serverMessage as string
        )

        return new ApiError({
            ...errorInfo,
            status,
            originalError: axiosError,
            context: {
                ...context,
                responseData,
                url: axiosError.config?.url,
                method: axiosError.config?.method,
            },
        })
    }

    // Handle generic errors
    if (error instanceof Error) {
        return new ApiError({
            code: ApiErrorCode.UNKNOWN,
            message: error.message,
            userMessage: USER_MESSAGES[ApiErrorCode.UNKNOWN],
            retryable: false,
            originalError: error,
            context,
        })
    }

    // Handle unknown error types
    return new ApiError({
        code: ApiErrorCode.UNKNOWN,
        message: String(error),
        userMessage: USER_MESSAGES[ApiErrorCode.UNKNOWN],
        retryable: false,
        context,
    })
}

function isAxiosError(error: unknown): error is AxiosError {
    return (
        error !== null &&
        typeof error === 'object' &&
        'isAxiosError' in error &&
        (error as AxiosError).isAxiosError === true
    )
}

function getErrorInfoFromStatus(
    status: number,
    serverMessage?: string
): Omit<ApiErrorDetails, 'status' | 'originalError' | 'context'> {
    switch (status) {
        case 400:
            return {
                code: ApiErrorCode.BAD_REQUEST,
                message: serverMessage || 'Bad request',
                userMessage:
                    serverMessage || USER_MESSAGES[ApiErrorCode.BAD_REQUEST],
                retryable: false,
            }
        case 401:
            return {
                code: ApiErrorCode.UNAUTHORIZED,
                message: serverMessage || 'Unauthorized',
                userMessage: USER_MESSAGES[ApiErrorCode.UNAUTHORIZED],
                retryable: false,
            }
        case 403:
            return {
                code: ApiErrorCode.FORBIDDEN,
                message: serverMessage || 'Forbidden',
                userMessage: USER_MESSAGES[ApiErrorCode.FORBIDDEN],
                retryable: false,
            }
        case 404:
            return {
                code: ApiErrorCode.NOT_FOUND,
                message: serverMessage || 'Not found',
                userMessage: USER_MESSAGES[ApiErrorCode.NOT_FOUND],
                retryable: false,
            }
        case 422:
            return {
                code: ApiErrorCode.VALIDATION_ERROR,
                message: serverMessage || 'Validation error',
                userMessage:
                    serverMessage ||
                    USER_MESSAGES[ApiErrorCode.VALIDATION_ERROR],
                retryable: false,
            }
        case 429:
            return {
                code: ApiErrorCode.RATE_LIMITED,
                message: serverMessage || 'Rate limited',
                userMessage: USER_MESSAGES[ApiErrorCode.RATE_LIMITED],
                retryable: true,
            }
        case 500:
            return {
                code: ApiErrorCode.SERVER_ERROR,
                message: serverMessage || 'Internal server error',
                userMessage: USER_MESSAGES[ApiErrorCode.SERVER_ERROR],
                retryable: true,
            }
        case 502:
        case 503:
        case 504:
            return {
                code: ApiErrorCode.SERVICE_UNAVAILABLE,
                message: serverMessage || 'Service unavailable',
                userMessage: USER_MESSAGES[ApiErrorCode.SERVICE_UNAVAILABLE],
                retryable: true,
            }
        default:
            if (status >= 500) {
                return {
                    code: ApiErrorCode.SERVER_ERROR,
                    message: serverMessage || `Server error (${status})`,
                    userMessage: USER_MESSAGES[ApiErrorCode.SERVER_ERROR],
                    retryable: true,
                }
            }
            return {
                code: ApiErrorCode.UNKNOWN,
                message: serverMessage || `HTTP error ${status}`,
                userMessage: USER_MESSAGES[ApiErrorCode.UNKNOWN],
                retryable: false,
            }
    }
}

/**
 * Check if an error is a RichApi.Error (Excel/Office API error)
 */
export function isRichApiError(error: unknown): boolean {
    if (!error) return false

    if (error instanceof Error) {
        return (
            error.name === 'RichApi.Error' ||
            error.message?.includes('RichApi.Error') ||
            error.message?.includes('Wait until the previous call completes') ||
            error.message?.includes('GeneralException') ||
            error.message?.includes('Excel') ||
            error.message?.includes('Office')
        )
    }

    return false
}

/**
 * Check if an error is a startup permission error (expected, shouldn't spam Datadog)
 * These are common when users don't have permissions to set startup behavior
 */
export function isStartupPermissionError(error: unknown): boolean {
    if (!error) return false

    if (error instanceof Error) {
        const message = error.message || ''
        return (
            message.includes('You cannot perform the requested operation') ||
            message.includes("You don't have sufficient permissions") ||
            message.includes('sufficient permissions')
        )
    }

    return false
}

/**
 * Check if an error is a formula parsing error (not critical, shouldn't spam Datadog)
 */
export function isFormulaParsingError(error: unknown): boolean {
    if (!error) return false

    if (error instanceof Error) {
        // ReferenceError from undefined function names
        if (
            error.name === 'ReferenceError' &&
            (error.message?.includes('WISE is not defined') ||
                error.message?.includes('WISEPRICE is not defined') ||
                error.message?.includes('FMP is not defined'))
        ) {
            return true
        }

        // SyntaxError from malformed operations
        if (
            error.name === 'SyntaxError' &&
            (error.message?.includes('Unexpected string') ||
                error.message?.includes('Unexpected token') ||
                error.message?.includes('Unexpected identifier'))
        ) {
            return true
        }
    }

    return false
}

/**
 * Parse a RichApi error into a standardized ApiError
 */
export function parseRichApiError(
    error: Error,
    context?: Record<string, unknown>
): ApiError {
    const message = error.message || error.toString()

    // "Wait until the previous call completes" is a common transient error
    if (message.includes('Wait until the previous call completes')) {
        return new ApiError({
            code: ApiErrorCode.EXCEL_BUSY,
            message: 'Excel is busy processing another operation',
            userMessage: USER_MESSAGES[ApiErrorCode.EXCEL_BUSY],
            retryable: true,
            originalError: error,
            context: {
                ...context,
                errorType: 'RichApi.Error',
                operation: context?.operation || 'unknown',
            },
        })
    }

    // General Excel/Office error
    return new ApiError({
        code: ApiErrorCode.EXCEL_ERROR,
        message: message,
        userMessage: USER_MESSAGES[ApiErrorCode.EXCEL_ERROR],
        retryable: true,
        originalError: error,
        context: {
            ...context,
            errorType: 'RichApi.Error',
        },
    })
}

/**
 * Enhanced error handler that checks for RichApi errors first
 */
export function handleError(
    error: unknown,
    context?: Record<string, unknown>
): ApiError {
    // Check for startup permission errors (expected, not critical)
    if (isStartupPermissionError(error)) {
        return new ApiError({
            code: ApiErrorCode.STARTUP_PERMISSION_ERROR,
            message: (error as Error).message || 'Startup permission denied',
            userMessage: USER_MESSAGES[ApiErrorCode.STARTUP_PERMISSION_ERROR],
            retryable: false,
            originalError: error as Error,
            context: {
                ...context,
                errorType: 'StartupPermissionError',
            },
        })
    }

    // Check for formula parsing errors (treat similar to Excel busy - not critical)
    if (isFormulaParsingError(error)) {
        return new ApiError({
            code: ApiErrorCode.EXCEL_ERROR,
            message: 'Formula parsing error',
            userMessage: 'There was an issue parsing a formula operation.',
            retryable: false,
            originalError: error as Error,
            context: {
                ...context,
                errorType: 'FormulaParsingError',
            },
        })
    }

    if (isRichApiError(error)) {
        return parseRichApiError(error as Error, context)
    }

    return parseApiError(error, context)
}

/**
 * Log error to Datadog with context
 */
export function logApiError(
    error: ApiError,
    additionalContext?: Record<string, unknown>
) {
    const logData = {
        errorCode: error.code,
        errorMessage: error.message,
        httpStatus: error.status,
        retryable: error.retryable,
        ...error.context,
        ...additionalContext,
    }

    // For Excel busy errors, only log to console (not Datadog) since they're expected and transient
    if (error.code === ApiErrorCode.EXCEL_BUSY) {
        console.log('[Excel Busy]', error.message, additionalContext)
        return
    }

    // For startup permission errors, only log to console (not Datadog) since they're expected
    if (error.code === ApiErrorCode.STARTUP_PERMISSION_ERROR) {
        console.log('[Startup Permission]', error.message, additionalContext)
        return
    }

    // For formula parsing errors, only log to console (not Datadog) since they're edge cases
    if (error.context?.errorType === 'FormulaParsingError') {
        console.log('[Formula Parsing]', error.message, additionalContext)
        return
    }

    const url = error.context?.url || 'unknown'
    const method = (error.context?.method as string)?.toUpperCase() || 'UNKNOWN'
    const statusText = error.status ? ` ${error.status}` : ''
    const logMessage = `[API Error] ${error.code}${statusText} - ${method} ${url}: ${error.message}`

    if (process.env.NODE_ENV === 'production' && datadogLogs) {
        try {
            datadogLogs.logger.error(
                logMessage,
                logData,
                error.originalError || error
            )
        } catch (err) {
            console.error(logMessage, logData)
            console.warn('Failed to log to Datadog:', err)
        }
    } else {
        // In development, log to console for debugging
        console.error(logMessage, logData)
    }
}

/**
 * Handle and log an error in one call
 * Returns the parsed error for further handling
 */
export function handleAndLogError(
    error: unknown,
    context?: Record<string, unknown>
): ApiError {
    const parsedError = handleError(error, context)
    logApiError(parsedError, context)
    return parsedError
}
